import { Button } from 'primeng/button';
import { Ripple } from 'primeng/ripple';
import { NgClass } from '@angular/common';
import { Tooltip } from 'primeng/tooltip';
import { InputText } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { catchError, finalize, of, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';

import { SignInForm } from '../../models/auth.interfaces';
import { AuthService } from '../../../../core/services/auth.service';
import { environment } from '../../../../../environments/environment';
import { sanitizeReturnUrl } from '../../../../shared/utils/url.util';
import { AuthProvider } from '../../../../shared/models/user/user.enums';
import { FieldInvalidPipe } from '../../../../shared/pipes/field-invalid.pipe';
import { NotificationService } from '../../../../core/services/notification.service';
import { ToLowercaseDirective } from '../../../../shared/directives/to-lowercase/to-lowercase.directive';
import {
	AUTH_PROVIDER_LABELS,
	AUTH_RETURN_URL_SESSION_STORAGE_KEY,
	AUTH_SIGN_IN_LOCAL_LABEL,
	AUTH_SIGN_IN_PATHS,
	ExternalAuthProvider
} from '../../../../shared/models/auth/auth.constants';

@Component({
	selector: 'app-sign-in-form',
	imports: [
		ReactiveFormsModule,
		InputText,
		Button,
		Ripple,
		NgClass,
		FieldInvalidPipe,
		Tooltip,
		ToLowercaseDirective,
		PasswordModule
	],
	templateUrl: './sign-in-form.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignInFormComponent {
	public isLoading = signal(false);
	public showLocalSignIn = signal(false);

	private readonly router = inject(Router);
	private readonly destroyRef = inject(DestroyRef);
	private readonly authService = inject(AuthService);
	private readonly fb = inject(FormBuilder);
	private readonly route = inject(ActivatedRoute);
	private readonly notificationService = inject(NotificationService);

	public readonly AuthProvider = AuthProvider;
	public readonly isMsaEnabled = environment.msaConfig.isEnabled;
	public readonly isAzureEnabled = environment.azureConfig.isEnabled;
	public readonly isGoogleEnabled = environment.googleConfig.isEnabled;
	public readonly AUTH_PROVIDER_LABELS = AUTH_PROVIDER_LABELS;
	public readonly AUTH_SIGN_IN_LOCAL_LABEL = AUTH_SIGN_IN_LOCAL_LABEL;

	public loginForm: FormGroup<SignInForm> = this.fb.nonNullable.group({
		username: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(64)]],
		password: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(96)]]
	});

	/**
	 * Handles login form submission. Authenticates user and redirects to returnUrl on success.
	 */
	public onSubmit(): void {
		if (this.loginForm.invalid) {
			this.loginForm.markAllAsTouched();
			return;
		}

		this.isLoading.set(true);

		this.authService
			.signIn(this.loginForm.getRawValue())
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				tap(() => this.notificationService.success('Sign in successfully!')),
				catchError((errorResponse: HttpErrorResponse) => {
					this.notificationService.error(errorResponse?.error?.message, errorResponse?.error?.error);
					return of(null);
				}),
				finalize(() => this.isLoading.set(false))
			)
			.subscribe((result) => {
				if (result) {
					const returnUrl = this.getReturnUrl();
					void this.router.navigateByUrl(returnUrl);
				}
			});
	}

	/**
	 * Reveals the local username/password sign-in form.
	 *
	 * @return {void} No return value.
	 */
	public onShowLocalSignIn(): void {
		this.showLocalSignIn.set(true);
	}

	/**
	 * Initiates external auth sign-in flow (Azure AD or MSA).
	 * Stores returnUrl for post-OAuth redirect.
	 *
	 * @param {ExternalAuthProvider} provider - The auth provider.
	 */
	public signInWithProvider(provider: ExternalAuthProvider): void {
		const returnUrl = this.getReturnUrl();
		if (returnUrl !== '/') sessionStorage.setItem(AUTH_RETURN_URL_SESSION_STORAGE_KEY, returnUrl);

		window.location.href = `${environment.apiBaseUrl}${AUTH_SIGN_IN_PATHS[provider]}`;
	}

	/**
	 * Gets and sanitizes returnUrl from query params to prevent open redirect attacks.
	 *
	 * @returns {string} The sanitized returnUrl.
	 */
	private getReturnUrl(): string {
		return sanitizeReturnUrl(this.route.snapshot.queryParams['returnUrl']);
	}
}
